import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Inbox as InboxIcon, Loader2, Search, ArrowDownLeft, ArrowUpRight,
  MailOpen, MousePointerClick, CornerDownLeft,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 30;

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    return sameDay
      ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};
const initials = (name, email) => {
  const s = (name || email || '?').trim();
  const parts = s.split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};

const Inbox = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState('all'); // all | in | out
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [stats, setStats] = useState({ sent: 0, received: 0, logged: 0 });

  useEffect(() => { const h = setTimeout(() => setDebounced(search), 350); return () => clearTimeout(h); }, [search]);
  useEffect(() => { setOffset(0); }, [direction, debounced]);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inbox`, { params: { direction, search: debounced || undefined, limit: PAGE, offset } });
      setItems(res.data.items || []);
      setHasMore(res.data.has_more);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [direction, debounced, offset]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);
  useEffect(() => { axios.get(`${API}/inbox/stats`).then(r => setStats(r.data || {})).catch(() => {}); }, []);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="inbox-page">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <InboxIcon className="w-6 h-6 text-violet-600" /> Inbox
        </h1>
        <p className="text-slate-400">A unified, two-way view of every email your team has sent and received across the workspace.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 bg-slate-900/60 border border-slate-800 rounded-lg p-1">
          {[['all', 'All'], ['in', 'Received'], ['out', 'Sent']].map(([v, l]) => (
            <button key={v} onClick={() => setDirection(v)}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${direction === v ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white'}`}
              data-testid={`inbox-filter-${v}`}>{l}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email or subject…" className="pl-9 bg-slate-900 border-slate-800 text-white" data-testid="inbox-search" />
        </div>
        <span className="text-xs text-slate-500 ml-auto" data-testid="inbox-stats">
          {(stats.sent || 0).toLocaleString()} sent · {(stats.received || 0).toLocaleString()} received
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-slate-800 rounded-2xl" data-testid="inbox-empty">
          <InboxIcon className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-medium">No messages</p>
          <p className="text-slate-500 text-sm">Sent campaigns, replies and Outlook-synced emails will appear here.</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-800 border border-slate-800 rounded-xl overflow-hidden" data-testid="inbox-list">
          {items.map(i => (
            <div key={i.id}
              onClick={() => i.contact_id && navigate(`/admin/contacts?open=${i.contact_id}`)}
              className={`flex items-start gap-3 px-4 py-3 bg-slate-900/40 hover:bg-slate-800/40 transition-colors ${i.contact_id ? 'cursor-pointer' : ''}`}
              data-testid={`inbox-item-${i.id}`}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${i.direction === 'in' ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-100 text-violet-700'}`}>
                {initials(i.counterparty_name, i.counterparty_email)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {i.direction === 'in'
                    ? <ArrowDownLeft className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    : <ArrowUpRight className="w-3.5 h-3.5 text-violet-600 shrink-0" />}
                  <span className="text-sm font-medium text-white truncate">{i.counterparty_name || i.counterparty_email || 'Unknown'}</span>
                  {i.company_name && <span className="text-xs text-slate-500 truncate hidden sm:inline">· {i.company_name}</span>}
                  {i.source === 'outlook' && <Badge variant="outline" className="text-[9px] bg-sky-100 text-sky-700 border-sky-200">Outlook</Badge>}
                  <span className="ml-auto text-[11px] text-slate-500 shrink-0">{fmtTime(i.at)}</span>
                </div>
                <p className="text-sm text-slate-300 truncate">{i.subject}</p>
                <p className="text-xs text-slate-500 truncate">{i.snippet}</p>
                <div className="flex items-center gap-2 mt-1">
                  {i.opened && <span className="text-[10px] text-slate-500 flex items-center gap-0.5"><MailOpen className="w-3 h-3" /> Opened</span>}
                  {i.clicked && <span className="text-[10px] text-sky-600 flex items-center gap-0.5"><MousePointerClick className="w-3 h-3" /> Clicked</span>}
                  {i.replied && <span className="text-[10px] text-emerald-600 flex items-center gap-0.5"><CornerDownLeft className="w-3 h-3" /> Replied</span>}
                  {i.sentiment && <Badge variant="outline" className="text-[9px] bg-slate-100 text-slate-600 border-slate-200 capitalize">{i.sentiment}</Badge>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(offset > 0 || hasMore) && !loading && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" className="bg-slate-900 border-slate-700" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} data-testid="inbox-prev">Newer</Button>
          <Button variant="outline" size="sm" className="bg-slate-900 border-slate-700" disabled={!hasMore} onClick={() => setOffset(offset + PAGE)} data-testid="inbox-next">Older</Button>
        </div>
      )}
    </div>
  );
};

export default Inbox;
